import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { learnCourse, completeLesson } from '../../services/student';
import { startQuiz, submitQuiz, getQuizAttempts } from '../../services/quiz';
import AdaptiveQuizPlayer from './AdaptiveQuizPlayer';
import ExercisePlayer from './ExercisePlayer';
import VideoPlayer from '../video/VideoPlayer';
import ProgressBar from '../ui/ProgressBar';
import Button from '../ui/Button';
import {
    Check, ArrowLeft, ArrowRight, Clock, AlertCircle, CheckCircle,
    XCircle, CircleDot, ToggleLeft, MessageSquare, ClipboardList, RotateCcw, Trophy,
    Download, FileText as FileTextIcon,
} from 'lucide-react';

/* ─── Quiz Player (Student) ─── */
function QuizPlayer({ test, onComplete }) {
    const [phase, setPhase] = useState('intro'); // intro | playing | result
    const [attempt, setAttempt] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [currentIdx, setCurrentIdx] = useState(0);
    const [answers, setAnswers] = useState({});
    const [timeLeft, setTimeLeft] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [attempts, setAttempts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Load past attempts
    useEffect(() => {
        getQuizAttempts(test.id)
            .then(r => setAttempts(r.data || []))
            .catch(() => {});
    }, [test.id]);

    // Timer
    useEffect(() => {
        if (phase !== 'playing' || timeLeft === null || timeLeft <= 0) return;
        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    handleSubmit();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [phase, timeLeft]);

    const handleStart = async () => {
        setLoading(true);
        setError(null);
        try {
            const r = await startQuiz(test.id);
            setAttempt(r.data.attempt);
            setQuestions(r.data.questions || []);
            setCurrentIdx(0);
            setAnswers({});
            if (r.data.time_limit_minutes) {
                setTimeLeft(r.data.time_limit_minutes * 60);
            }
            setPhase('playing');
        } catch (e) {
            setError(e.response?.data?.message || 'Testni boshlashda xatolik');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = useCallback(async () => {
        if (submitting || !attempt) return;
        setSubmitting(true);
        try {
            const r = await submitQuiz(attempt.id, answers);
            setResult(r.data);
            setPhase('result');
            // Refresh attempts
            getQuizAttempts(test.id).then(r2 => setAttempts(r2.data || [])).catch(() => {});
            if (r.data.passed) onComplete?.();
        } catch (e) {
            setError(e.response?.data?.message || 'Javoblarni yuborishda xatolik');
        } finally {
            setSubmitting(false);
        }
    }, [submitting, attempt, answers, test.id, onComplete]);

    const setAnswer = (questionId, value) => {
        setAnswers(prev => ({ ...prev, [questionId]: value }));
    };

    const formatTime = (secs) => {
        const m = Math.floor(secs / 60);
        const s = secs % 60;
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    const currentQ = questions[currentIdx];
    const answeredCount = Object.keys(answers).length;

    // ─── Intro Phase ───
    if (phase === 'intro') {
        return (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                        <ClipboardList className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">{test.title || 'Test'}</h3>
                        <p className="text-sm text-gray-500">{test.questions?.length || 0} ta savol</p>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-5 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                        <Clock className="w-4 h-4" />
                        <span>{test.time_limit_minutes ? `${test.time_limit_minutes} daqiqa` : 'Vaqt cheksiz'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                        <CheckCircle className="w-4 h-4" />
                        <span>O'tish balli: {test.passing_score}%</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                        <RotateCcw className="w-4 h-4" />
                        <span>Maks. urinish: {test.max_attempts}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                        <Trophy className="w-4 h-4" />
                        <span>Urinishlar: {attempts.length}/{test.max_attempts}</span>
                    </div>
                </div>

                {/* Past attempts */}
                {attempts.length > 0 && (
                    <div className="mb-5">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Oldingi natijalar</h4>
                        <div className="space-y-1.5">
                            {attempts.map((a, i) => (
                                <div key={a.id} className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm
                                    ${a.score >= test.passing_score ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                    <span>{i + 1}-urinish</span>
                                    <span className="font-semibold">{Math.round(a.score)}%</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {error && <p className="text-sm text-red-500 mb-3">{error}</p>}

                <button onClick={handleStart} disabled={loading || attempts.length >= test.max_attempts}
                    className="w-full py-3 bg-blue-600 text-white rounded-xl font-medium text-sm hover:bg-blue-700 disabled:opacity-50 transition flex items-center justify-center gap-2">
                    {loading ? 'Yuklanmoqda...' : attempts.length >= test.max_attempts ? 'Urinishlar tugadi' : 'Testni boshlash'}
                </button>
            </div>
        );
    }

    // ─── Result Phase ───
    if (phase === 'result' && result) {
        const passed = result.score >= test.passing_score;
        return (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-6">
                <div className={`text-center py-6 ${passed ? 'text-green-600' : 'text-red-500'}`}>
                    {passed ? <Trophy className="w-16 h-16 mx-auto mb-3" /> : <XCircle className="w-16 h-16 mx-auto mb-3" />}
                    <h3 className="text-2xl font-bold">{Math.round(result.score)}%</h3>
                    <p className="text-sm font-medium mt-1">{passed ? "Tabriklaymiz! Test muvaffaqiyatli topshirildi!" : `Test topshirilmadi. O'tish balli: ${test.passing_score}%`}</p>
                    {result.xp_awarded > 0 && (
                        <p className="text-xs mt-2 text-blue-600 font-medium">+{result.xp_awarded} XP olindi!</p>
                    )}
                </div>

                {/* Question details if show_results_immediately */}
                {result.details && (
                    <div className="space-y-3 mt-4 border-t pt-4">
                        {result.details.map((d, idx) => {
                            const isOpen = d.ai_score !== undefined && d.ai_score !== null;
                            const isCorrect = isOpen ? d.correct : d.correct;
                            const bgClass = isCorrect
                                ? 'bg-green-50 border-green-200'
                                : isOpen && d.ai_score > 0
                                    ? 'bg-yellow-50 border-yellow-200'
                                    : 'bg-red-50 border-red-200';
                            const Icon = isCorrect ? CheckCircle : XCircle;
                            const iconColor = isCorrect ? 'text-green-500' : isOpen && d.ai_score > 0 ? 'text-yellow-500' : 'text-red-500';

                            return (
                                <div key={idx} className={`p-3 rounded-xl border ${bgClass}`}>
                                    <div className="flex items-start gap-2">
                                        <Icon className={`w-4 h-4 ${iconColor} mt-0.5 flex-shrink-0`} />
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-gray-900">{d.question}</p>
                                            <p className="text-xs text-gray-500 mt-0.5">Sizning javobingiz: {d.your_answer || '—'}</p>
                                            {!isCorrect && d.correct_answer && <p className="text-xs text-green-700 mt-0.5">To'g'ri javob: {d.correct_answer}</p>}
                                            {isOpen && (
                                                <div className="mt-1.5 space-y-1">
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                                            <div className={`h-1.5 rounded-full ${d.ai_score >= 60 ? 'bg-green-500' : d.ai_score > 0 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                                                style={{ width: `${d.ai_score}%` }} />
                                                        </div>
                                                        <span className="text-xs font-medium text-gray-600">{d.ai_score}%</span>
                                                    </div>
                                                    {d.ai_feedback && <p className="text-xs text-gray-600 italic">AI: {d.ai_feedback}</p>}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="flex gap-2 mt-5">
                    {attempts.length < test.max_attempts && !passed && (
                        <button onClick={() => { setPhase('intro'); setResult(null); setError(null); }}
                            className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition flex items-center justify-center gap-1.5">
                            <RotateCcw className="w-4 h-4" /> Qayta urinish
                        </button>
                    )}
                    <button onClick={() => { setPhase('intro'); setResult(null); }}
                        className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition">
                        Orqaga
                    </button>
                </div>
            </div>
        );
    }

    // ─── Playing Phase ───
    return (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm mb-6">
            {/* Header with timer and progress */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-500">{currentIdx + 1}/{questions.length}</span>
                    <div className="w-32 h-1.5 bg-gray-200 rounded-full">
                        <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${((currentIdx + 1) / questions.length) * 100}%` }} />
                    </div>
                </div>
                {timeLeft !== null && (
                    <div className={`flex items-center gap-1 text-sm font-mono font-medium ${timeLeft < 60 ? 'text-red-500' : 'text-gray-600'}`}>
                        <Clock className="w-4 h-4" /> {formatTime(timeLeft)}
                    </div>
                )}
            </div>

            {/* Question */}
            {currentQ && (
                <div className="p-6">
                    <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium
                            ${currentQ.type === 'multiple_choice' ? 'bg-purple-100 text-purple-700' :
                              currentQ.type === 'true_false' ? 'bg-cyan-100 text-cyan-700' :
                              'bg-amber-100 text-amber-700'}`}>
                            {currentQ.type === 'multiple_choice' ? "Ko'p tanlovli" : currentQ.type === 'true_false' ? "To'g'ri/Noto'g'ri" : 'Ochiq javob'}
                        </span>
                        <span className="text-xs text-gray-400">{currentQ.points} ball</span>
                    </div>
                    <h4 className="text-base font-semibold text-gray-900 mb-4 mt-2">{currentQ.text}</h4>

                    {/* Multiple choice */}
                    {currentQ.type === 'multiple_choice' && currentQ.answers && (
                        <div className="space-y-2">
                            {currentQ.answers.map(ans => {
                                const selected = answers[currentQ.id] === ans.id;
                                return (
                                    <button key={ans.id} onClick={() => setAnswer(currentQ.id, ans.id)}
                                        className={`w-full text-left px-4 py-3 rounded-xl border-2 text-sm transition flex items-center gap-3
                                            ${selected ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300 text-gray-700'}`}>
                                        <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0
                                            ${selected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                                            {selected && <Check className="w-3 h-3 text-white" />}
                                        </span>
                                        {ans.text}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* True/False */}
                    {currentQ.type === 'true_false' && currentQ.answers && (
                        <div className="flex gap-3">
                            {currentQ.answers.map(ans => {
                                const selected = answers[currentQ.id] === ans.id;
                                return (
                                    <button key={ans.id} onClick={() => setAnswer(currentQ.id, ans.id)}
                                        className={`flex-1 py-4 rounded-xl border-2 text-sm font-medium transition flex items-center justify-center gap-2
                                            ${selected ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300 text-gray-600'}`}>
                                        {ans.text === "To'g'ri" ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                                        {ans.text}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Open answer */}
                    {currentQ.type === 'open' && (
                        <textarea value={answers[currentQ.id] || ''} onChange={e => setAnswer(currentQ.id, e.target.value)}
                            rows={4} placeholder="Javobingizni yozing..."
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-blue-500 resize-none" />
                    )}
                </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between p-4 border-t border-gray-100">
                <button onClick={() => setCurrentIdx(i => Math.max(0, i - 1))} disabled={currentIdx === 0}
                    className="px-4 py-2 text-sm text-gray-600 rounded-xl border border-gray-200 hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1">
                    <ArrowLeft className="w-4 h-4" /> Oldingi
                </button>
                <div className="flex gap-1">
                    {questions.map((_, i) => (
                        <button key={i} onClick={() => setCurrentIdx(i)}
                            className={`w-7 h-7 rounded-lg text-xs font-medium transition
                                ${i === currentIdx ? 'bg-blue-600 text-white' :
                                  answers[questions[i]?.id] !== undefined ? 'bg-green-100 text-green-700' :
                                  'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                            {i + 1}
                        </button>
                    ))}
                </div>
                {currentIdx < questions.length - 1 ? (
                    <button onClick={() => setCurrentIdx(i => Math.min(questions.length - 1, i + 1))}
                        className="px-4 py-2 text-sm text-blue-600 rounded-xl border border-blue-200 hover:bg-blue-50 font-medium flex items-center gap-1">
                        Keyingi <ArrowRight className="w-4 h-4" />
                    </button>
                ) : (
                    <button onClick={handleSubmit} disabled={submitting}
                        className="px-5 py-2 text-sm bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 disabled:opacity-50 flex items-center gap-1">
                        {submitting ? 'Yuborilmoqda...' : <><Check className="w-4 h-4" /> Yakunlash ({answeredCount}/{questions.length})</>}
                    </button>
                )}
            </div>

            {error && <p className="text-sm text-red-500 px-4 pb-4">{error}</p>}
        </div>
    );
}

export default function LearnView() {
    const { courseId } = useParams();
    const [course, setCourse] = useState(null);
    const [progress, setProgress] = useState({});
    const [activeLesson, setActiveLesson] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        learnCourse(courseId)
            .then((res) => {
                setCourse(res.data.course);
                setProgress(res.data.progress || {});

                // Auto-select first lesson
                const firstModule = res.data.course?.modules?.[0];
                if (firstModule?.lessons?.[0]) {
                    setActiveLesson(firstModule.lessons[0]);
                }
            })
            .finally(() => setLoading(false));
    }, [courseId]);

    const allLessons = course?.modules?.flatMap((m) => m.lessons) || [];
    const completedCount = Object.values(progress).filter((p) => p.status === 'completed').length;
    const progressPercent = allLessons.length > 0 ? Math.round((completedCount / allLessons.length) * 100) : 0;

    const handleComplete = async () => {
        if (!activeLesson) return;
        const res = await completeLesson(activeLesson.id);
        setProgress((prev) => ({ ...prev, [activeLesson.id]: res.data }));

        // Move to next lesson
        const currentIdx = allLessons.findIndex((l) => l.id === activeLesson.id);
        if (currentIdx < allLessons.length - 1) {
            setActiveLesson(allLessons[currentIdx + 1]);
        }
    };

    const handlePrev = () => {
        const currentIdx = allLessons.findIndex((l) => l.id === activeLesson.id);
        if (currentIdx > 0) setActiveLesson(allLessons[currentIdx - 1]);
    };

    const handleNext = () => {
        const currentIdx = allLessons.findIndex((l) => l.id === activeLesson.id);
        if (currentIdx < allLessons.length - 1) setActiveLesson(allLessons[currentIdx + 1]);
    };

    if (loading) {
        return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
    }

    if (!course) return <p className="text-center text-gray-500 py-12">Kurs topilmadi</p>;

    const contents = activeLesson?.contents || [];
    const videoContent = contents.find(c => c.content_type === 'hls_video');
    const htmlContent = contents.find(c => c.content_type === 'html');
    const fileContent = contents.find(c => c.content_type === 'file_path');
    const videoUrlContent = contents.find(c => c.content_type === 'video_url');

    return (
        <div className="flex -mx-6 -mb-6 min-h-[calc(100vh-4rem)]">
            {/* Sidebar */}
            <div className="w-72 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 p-4 overflow-y-auto flex-shrink-0 h-[calc(100vh-5rem)]" style={{position:'sticky',top:'0'}}>
                <h2 className="font-bold text-gray-900 mb-2">{course.title}</h2>
                <ProgressBar value={progressPercent} className="mb-4" />
                <p className="text-xs text-gray-500 mb-4">{completedCount}/{allLessons.length} dars — {progressPercent}%</p>

                {course.modules?.map((module) => (
                    <div key={module.id} className="mb-4">
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">{module.title}</h3>
                        {module.lessons?.map((lesson) => {
                            const isActive = activeLesson?.id === lesson.id;
                            const isCompleted = progress[lesson.id]?.status === 'completed';

                            return (
                                <button
                                    key={lesson.id}
                                    onClick={() => setActiveLesson(lesson)}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm mb-1 flex items-center gap-2 transition-colors ${
                                        isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
                                    }`}
                                >
                                    <span className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center text-xs ${
                                        isCompleted ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300'
                                    }`}>
                                        {isCompleted && <Check className="w-3 h-3" />}
                                    </span>
                                    <span className="truncate">{lesson.title}</span>
                                </button>
                            );
                        })}
                    </div>
                ))}
            </div>

            {/* Content */}
            <div className="flex-1 p-6 overflow-y-auto">
                {activeLesson ? (
                    <>
                        <h1 className="text-xl font-bold text-gray-900 mb-4">{activeLesson.title}</h1>

                        {/* HLS Video */}
                        {videoContent && (() => {
                            try {
                                const data = JSON.parse(videoContent.content_data);
                                return (
                                    <div className="mb-6">
                                        <VideoPlayer src={`/storage/${data.playlist}`} />
                                    </div>
                                );
                            } catch { return null; }
                        })()}

                        {/* External Video URL */}
                        {videoUrlContent && (
                            <div className="aspect-video mb-6">
                                <iframe
                                    src={videoUrlContent.content_data}
                                    className="w-full h-full rounded-lg"
                                    allowFullScreen
                                />
                            </div>
                        )}

                        {/* HTML Text Content */}
                        {htmlContent?.content_data && (
                            <div
                                className="prose dark:prose-invert max-w-none mb-6"
                                dangerouslySetInnerHTML={{ __html: htmlContent.content_data }}
                            />
                        )}

                        {/* File / PDF / Office Content */}
                        {fileContent && (() => {
                            let files = [];
                            try {
                                const data = JSON.parse(fileContent.content_data);
                                if (Array.isArray(data)) {
                                    files = data;
                                } else if (data.path) {
                                    files = [data];
                                }
                            } catch {
                                files = [{ path: fileContent.content_data, original_name: fileContent.content_data.split('/').pop() }];
                            }

                            const getExt = (name) => (name || '').split('.').pop().toLowerCase();
                            const getIcon = (name) => {
                                const ext = getExt(name);
                                if (ext === 'pdf') return '📄';
                                if (['ppt', 'pptx'].includes(ext)) return '📊';
                                if (['doc', 'docx'].includes(ext)) return '📝';
                                if (['xls', 'xlsx'].includes(ext)) return '📈';
                                if (ext === 'zip') return '📦';
                                return '📎';
                            };
                            const isOffice = (name) => ['ppt', 'pptx', 'doc', 'docx', 'xls', 'xlsx'].includes(getExt(name));
                            const getPublicUrl = (path) => `${window.location.origin}/storage/${path}`;

                            return (
                                <div className="mb-6 space-y-4">
                                    {files.map((f, idx) => {
                                        const ext = getExt(f.original_name);
                                        const isPdf = ext === 'pdf';
                                        const isOff = isOffice(f.original_name);

                                        if (isPdf) {
                                            return (
                                                <div key={idx}>
                                                    <iframe
                                                        src={`/storage/${f.path}`}
                                                        className="w-full rounded-lg border border-gray-200 dark:border-gray-700"
                                                        style={{ height: '80vh' }}
                                                        title={f.original_name || 'PDF'}
                                                    />
                                                    <div className="mt-2">
                                                        <a href={`/storage/${f.path}`} target="_blank" rel="noopener noreferrer"
                                                            className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
                                                            <Download className="w-4 h-4" /> {f.original_name}
                                                        </a>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        if (isOff) {
                                            const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(getPublicUrl(f.path))}`;
                                            return (
                                                <div key={idx}>
                                                    <iframe
                                                        src={viewerUrl}
                                                        className="w-full rounded-lg border border-gray-200 dark:border-gray-700"
                                                        style={{ height: '80vh' }}
                                                        title={f.original_name}
                                                    />
                                                    <div className="mt-2">
                                                        <a href={`/storage/${f.path}`} target="_blank" rel="noopener noreferrer"
                                                            className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
                                                            <Download className="w-4 h-4" /> {f.original_name}
                                                        </a>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        return (
                                            <div key={idx} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 flex items-center gap-3">
                                                <span className="text-3xl shrink-0">{getIcon(f.original_name)}</span>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">{f.original_name}</p>
                                                    <a href={`/storage/${f.path}`} target="_blank" rel="noopener noreferrer"
                                                        className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
                                                        <Download className="w-4 h-4" /> Yuklab olish
                                                    </a>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })()}

                        {/* Interactive exercises */}
                        {activeLesson.exercises?.length > 0 && activeLesson.exercises.map((exercise) => (
                            <ExercisePlayer key={exercise.id} exercise={exercise} />
                        ))}

                        {/* Quiz / Test */}
                        {activeLesson.test && activeLesson.test.questions?.length > 0 && (
                            activeLesson.test.is_adaptive ? (
                                <AdaptiveQuizPlayer test={activeLesson.test} onComplete={handleComplete} />
                            ) : (
                                <QuizPlayer test={activeLesson.test} onComplete={handleComplete} />
                            )
                        )}

                        <div className="flex items-center gap-3 pt-4 border-t">
                            <Button variant="secondary" onClick={handlePrev} disabled={allLessons.findIndex((l) => l.id === activeLesson.id) === 0}>
                                <ArrowLeft className="w-4 h-4 inline mr-1" /> Oldingi
                            </Button>
                            <Button onClick={handleComplete}>
                                Darsni tugatish <Check className="w-4 h-4 inline ml-1" />
                            </Button>
                            <Button variant="secondary" onClick={handleNext} disabled={allLessons.findIndex((l) => l.id === activeLesson.id) === allLessons.length - 1}>
                                Keyingi <ArrowRight className="w-4 h-4 inline ml-1" />
                            </Button>
                        </div>
                    </>
                ) : (
                    <p className="text-gray-500 text-center py-12">Darsni tanlang</p>
                )}
            </div>
        </div>
    );
}
